import { type Pinia } from 'pinia'
import { Ref, watch } from 'vue'
import DOMPurify from 'dompurify'

import { useSearchQueryStore } from '@/store/SearchQueryStore'
import { usePopupStore } from '@/store/AutocompletePopup/PopupStore'
import { useUrlStore } from '@/store/SerpPage/UrlStore'
import { useMobileViewStore } from '@/store/MobileViewStore'
import { useBridgeStore, BODY_PLACEMENT_ATTRIBUTE, INPUT_ATTRIBUTE, INPUT_CLASS, ROOT_ATTRIBUTE, WRAPPER_ATTRIBUTE } from '@/store/BridgeStore'

import { searchErrorLogger, searchLogger } from '@/core/Logger'
import { SmartSearchConfigInterface, SmartSearchTranslationsInterface } from '@/core/types'

interface AutocompleteBridgeOptions {
  pinia: Pinia
  smartSearchConfig: Ref<SmartSearchConfigInterface>
  smartSearchTranslations: Ref<SmartSearchTranslationsInterface>
}

type ListenerCleanup = () => void

interface BindResult {
  success: boolean
}

interface ListenerDescriptor {
  type: keyof HTMLElementEventMap | string
  listener: (event: Event) => void
  options?: boolean | AddEventListenerOptions
}

interface StripEventListenersOptions {
  removeAll?: boolean
  eventTypes?: string[]
}

interface WaitForSelectorOptions {
  maxAttempts: number
  intervalMs: number
}

interface BridgeBindingBaseOptions {
  replaceWithClone?: boolean
  observeViewport?: boolean
  wait?: Partial<WaitForSelectorOptions>
}

interface InputBridgeOptions extends BridgeBindingBaseOptions {
  treatTabletViewAsMobile?: boolean
  forceBodyPlacementBelowViewportWidth?: number
}

interface TriggerBindingOptions extends BridgeBindingBaseOptions {
  activateBelowViewportWidth?: number | null
}

interface FocusAfterClickOptions extends BridgeBindingBaseOptions {
  delayMs?: number
  events?: Array<keyof HTMLElementEventMap | string>
}

const DEFAULT_WAIT_OPTIONS: WaitForSelectorOptions = {
  maxAttempts: 20,
  intervalMs: 250
}

const DEFAULT_TRIGGER_ACTIVATION_WIDTH = 992
const DEFAULT_FOCUS_DELAY_MS = 100
const DEFAULT_FOCUS_EVENTS: Array<keyof HTMLElementEventMap | string> = ['click', 'touchend']

type NormalizedBindingOptions<T extends BridgeBindingBaseOptions> = T & {
  replaceWithClone: boolean
  observeViewport: boolean
  wait: WaitForSelectorOptions
}

type BindingOptionsInput<T extends BridgeBindingBaseOptions> = boolean | T | undefined

function normalizeBindingOptions<T extends BridgeBindingBaseOptions> (
  optionsOrFlag: BindingOptionsInput<T>
): NormalizedBindingOptions<T> {
  const baseOptions: BridgeBindingBaseOptions = {}
  const specificOptions = (typeof optionsOrFlag === 'object' && optionsOrFlag)
    ? optionsOrFlag
    : {} as T

  if (typeof optionsOrFlag === 'boolean') {
    baseOptions.replaceWithClone = optionsOrFlag
  } else if (specificOptions.replaceWithClone !== undefined) {
    baseOptions.replaceWithClone = specificOptions.replaceWithClone
  }

  if (specificOptions.observeViewport !== undefined) {
    baseOptions.observeViewport = specificOptions.observeViewport
  }

  const waitOptions = specificOptions.wait ?? {}

  return {
    ...(specificOptions as T),
    replaceWithClone: baseOptions.replaceWithClone ?? true,
    observeViewport: baseOptions.observeViewport ?? true,
    wait: {
      maxAttempts: waitOptions.maxAttempts ?? DEFAULT_WAIT_OPTIONS.maxAttempts,
      intervalMs: waitOptions.intervalMs ?? DEFAULT_WAIT_OPTIONS.intervalMs
    }
  }
}

async function waitForSelector<T extends Element> (
  selector: string,
  guard: (element: Element | null) => element is T,
  options: WaitForSelectorOptions
): Promise<{ element: T | null, attempts: number }> {
  for (let attempt = 1; attempt <= options.maxAttempts; attempt++) {
    const candidate = document.querySelector(selector)
    if (guard(candidate)) {
      return { element: candidate, attempts: attempt }
    }
    await new Promise(resolve => setTimeout(resolve, options.intervalMs))
  }

  return { element: null, attempts: options.maxAttempts }
}

function preferBodyPlacement (
  mobileViewStore: ReturnType<typeof useMobileViewStore>,
  options: NormalizedBindingOptions<InputBridgeOptions>,
  bridgeStore?: ReturnType<typeof useBridgeStore>
): boolean {
  const forcedByStore = bridgeStore?.forceBodyPlacement === true
  const forcedByBreakpoint = typeof window !== 'undefined' && typeof options.forceBodyPlacementBelowViewportWidth === 'number'
    ? window.innerWidth <= options.forceBodyPlacementBelowViewportWidth
    : false

  return forcedByStore ||
    forcedByBreakpoint ||
    mobileViewStore.isMobileView ||
    (options.treatTabletViewAsMobile === true && mobileViewStore.isTabletView)
}

const CLICK_RELATED_EVENTS = [
  'click',
  'mousedown',
  'mouseup',
  'pointerdown',
  'pointerup',
  'pointercancel',
  'touchstart',
  'touchend',
  'touchcancel',
  'touchmove'
]

function normalizeEventHandlerProperty (property: unknown): string | null {
  if (typeof property === 'string') {
    return property
  }

  if (property != null && typeof property === 'object') {
    const potentialName = (property as { name?: unknown }).name
    if (typeof potentialName === 'string') {
      return potentialName
    }
  }

  return null
}

function collectEventHandlerProperties (element: HTMLElement): string[] {
  const properties = new Set<string>()
  let current: HTMLElement | null = element

  while (current && current !== Object.prototype) {
    Object.getOwnPropertyNames(current).forEach(property => {
      const normalizedProperty = normalizeEventHandlerProperty(property)

      if (normalizedProperty && normalizedProperty.startsWith('on') && normalizedProperty.length > 2) {
        properties.add(normalizedProperty)
      }
    })

    current = Object.getPrototypeOf(current) as HTMLElement | null
  }

  return Array.from(properties)
}

interface InputSelectionState {
  start: number | null
  end: number | null
  direction: 'forward' | 'backward' | 'none' | null
}

type SelectableElement = HTMLInputElement | HTMLTextAreaElement

function isSelectableElement (element: HTMLElement): element is SelectableElement {
  return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement
}

function preserveInputState (source: SelectableElement, target: SelectableElement): InputSelectionState | null {
  target.value = source.value

  if (source instanceof HTMLInputElement && target instanceof HTMLInputElement) {
    target.checked = source.checked

    if (source.type === 'file') {
      return null
    }
  }

  return {
    start: source.selectionStart,
    end: source.selectionEnd,
    direction: source.selectionDirection
  }
}

function replaceElementWithClone<T extends HTMLElement> (element: T): T {
  const clone = element.cloneNode(true) as T
  const wasFocused = document.activeElement === element

  let selectionState: InputSelectionState | null = null

  const originalRecord = element as HTMLElement & Record<string, unknown>
  const cloneRecord = clone as HTMLElement & Record<string, unknown>

  if ('value' in originalRecord && 'value' in cloneRecord) {
    try {
      cloneRecord.value = originalRecord.value
    } catch (error) {
      // Ignore assignment issues for readonly values
    }
  }

  if ('checked' in originalRecord && 'checked' in cloneRecord) {
    try {
      cloneRecord.checked = originalRecord.checked
    } catch (error) {
      // Ignore assignment issues for readonly values
    }
  }

  if (isSelectableElement(element) && isSelectableElement(clone)) {
    selectionState = preserveInputState(element, clone)
  }

  element.replaceWith(clone)

  if (wasFocused) {
    window.requestAnimationFrame(() => {
      if (!document.contains(clone)) {
        return
      }

      try {
        clone.focus()
      } catch (error) {
        // Ignore focus issues for non-focusable elements
      }

      if (selectionState && isSelectableElement(clone)) {
        try {
          if (selectionState.start !== null && selectionState.end !== null) {
            clone.setSelectionRange(
              selectionState.start,
              selectionState.end,
              selectionState.direction ?? undefined
            )
          }
        } catch (error) {
          // Not all inputs support selection APIs – ignore silently
        }
      }
    })
  }

  return clone
}

function stripElementEventListeners<T extends HTMLElement> (
  element: T,
  options: StripEventListenersOptions,
  config: { cloneNode?: boolean } = {}
): T {
  const { cloneNode = true } = config
  const { removeAll = false, eventTypes = [] } = options
  const lowerCaseEventTypes = eventTypes.map(eventType => eventType.toLowerCase())

  const originalElement = element
  const targetElement = cloneNode ? replaceElementWithClone(element) : element

  const eventHandlerProperties = collectEventHandlerProperties(originalElement)

  eventHandlerProperties.forEach(property => {
    const normalizedProperty = normalizeEventHandlerProperty(property)

    if (!normalizedProperty || !normalizedProperty.startsWith('on') || normalizedProperty.length <= 2) {
      return
    }

    const eventType = normalizedProperty.slice(2).toLowerCase()

    if (removeAll || lowerCaseEventTypes.includes(eventType)) {
      if (targetElement.hasAttribute(normalizedProperty)) {
        targetElement.removeAttribute(normalizedProperty)
      }
      try {
        (targetElement as HTMLElement & Record<string, unknown>)[normalizedProperty] = null
      } catch (error) {
        // Ignore assignment issues for readonly handlers
      }
      return
    }

    if (cloneNode && originalElement !== targetElement) {
      try {
        (targetElement as HTMLElement & Record<string, unknown>)[normalizedProperty] =
          (originalElement as HTMLElement & Record<string, unknown>)[normalizedProperty]
      } catch (error) {
        // Ignore assignment issues for readonly handlers
      }
    }
  })

  return targetElement
}

function createCleanupCollector (): { add: (cleanup: ListenerCleanup) => void, runAll: () => void, list: ListenerCleanup[] } {
  const cleanups: ListenerCleanup[] = []
  return {
    add: (cleanup: ListenerCleanup) => cleanups.push(cleanup),
    runAll: () => {
      while (cleanups.length) {
        const cleanupFn = cleanups.pop()
        if (cleanupFn) {
          cleanupFn()
        }
      }
    },
    list: cleanups
  }
}

function attachListeners (element: HTMLElement, descriptors: ListenerDescriptor[], collector: ReturnType<typeof createCleanupCollector>): void {
  descriptors.forEach(({ type, listener, options }) => {
    element.addEventListener(type, listener as EventListener, options)
    collector.add(() => element.removeEventListener(type, listener as EventListener, options))
  })
}

function preventClientAction (event: Event): void {
  if ('preventDefault' in event) {
    event.preventDefault()
  }
  if ('stopImmediatePropagation' in event) {
    event.stopImmediatePropagation()
  }
  if ('stopPropagation' in event) {
    event.stopPropagation()
  }
}

function ensureAutocompleteRoot (wrapper: HTMLElement, input: HTMLElement, preferBodyPlacement: boolean): HTMLElement {
  let root = document.getElementById('smartsearch-autocomplete')
  if (!root) {
    root = document.createElement('div')
    root.id = 'smartsearch-autocomplete'
    searchLogger.log('Created smartsearch-autocomplete container for bridge')
  }

  root.setAttribute(ROOT_ATTRIBUTE, 'true')

  if (preferBodyPlacement) {
    root.setAttribute(BODY_PLACEMENT_ATTRIBUTE, 'true')
    document.body.setAttribute(BODY_PLACEMENT_ATTRIBUTE, 'true')
    document.documentElement?.setAttribute(BODY_PLACEMENT_ATTRIBUTE, 'true')
    if (root.parentElement !== document.body) {
      document.body.appendChild(root)
      searchLogger.log('Teleported smartsearch-autocomplete container to body for compact view')
    }
    return root
  }

  root.removeAttribute(BODY_PLACEMENT_ATTRIBUTE)
  document.body.removeAttribute(BODY_PLACEMENT_ATTRIBUTE)
  document.documentElement?.removeAttribute(BODY_PLACEMENT_ATTRIBUTE)
  if (root.parentElement !== wrapper) {
    wrapper.insertBefore(root, input.nextSibling)
    searchLogger.log('Moved smartsearch-autocomplete container under bridged input')
  }

  return root
}

function syncInputValueWithStore (input: HTMLInputElement, collector: ReturnType<typeof createCleanupCollector>, pinia: Pinia): void {
  const searchQueryStore = useSearchQueryStore(pinia)
  const unsubscribe = searchQueryStore.$subscribe((_, state) => {
    if (input.value !== state.searchQuery) {
      input.value = state.searchQuery
    }
  })
  collector.add(() => unsubscribe())
}

function createPerformSearch (pinia: Pinia, smartSearchConfig: Ref<SmartSearchConfigInterface>): () => void {
  return () => {
    const searchQueryStore = useSearchQueryStore(pinia)
    const urlStore = useUrlStore(pinia)
    const popupStore = usePopupStore(pinia)
    const mobileViewStore = useMobileViewStore(pinia)

    const query = searchQueryStore.searchQuery.trim()
    const customRedirects = window?._QONizer?.customSmartSearchConfig?.customRedirects || []
    const queryLower = query.toLowerCase()
    const matchedRedirect = customRedirects.find((redirect: { phrases: string[], url: string }) =>
      redirect.phrases.some((phrase: string) => phrase.toLowerCase().trim() === queryLower)
    )

    if (!query) {
      searchLogger.log('Bridge search aborted – empty query')
      popupStore.hideAutocomplete()
      return
    }

    searchLogger.log('Executing search via bridge', query)

    if (matchedRedirect) {
      searchLogger.log('Redirect matched via bridge', matchedRedirect.url)
      window.location.href = matchedRedirect.url
      return
    }

    const sanitised = DOMPurify.sanitize(query)
    const { redirectionUrl } = smartSearchConfig.value.appConfiguration

    urlStore.goToSerpPage(redirectionUrl, sanitised)

    if (mobileViewStore.isMobileView) {
      const bridgeStore = useBridgeStore(pinia)
      bridgeStore.focusInternalInput()
    }
  }
}

export function createAutocompleteBridge ({ pinia, smartSearchConfig, smartSearchTranslations }: AutocompleteBridgeOptions) {
  const bridgeStore = useBridgeStore(pinia)
  const performSearch = createPerformSearch(pinia, smartSearchConfig)

  async function bindInputBridge (selector: string, optionsOrFlag?: BindingOptionsInput<InputBridgeOptions>): Promise<BindResult> {
    const options = normalizeBindingOptions<InputBridgeOptions>(optionsOrFlag)
    const popupStore = usePopupStore(pinia)

    try {
      searchLogger.log(`bindInputBridge: waiting for selector ${selector}`)
      const { element: matchedElement, attempts } = await waitForSelector(
        selector,
        (candidate): candidate is HTMLInputElement => candidate instanceof HTMLInputElement,
        options.wait
      )

      if (!matchedElement) {
        searchLogger.log(`bindInputBridge: selector ${selector} not resolved after ${attempts} attempts`)
        searchErrorLogger.log(`bindInputBridge: Element not found for selector ${selector}`)
        return { success: false }
      }

      searchLogger.log(`bindInputBridge: selector ${selector} resolved after ${attempts} attempt(s)`)

      const baseElement = options.replaceWithClone
        ? replaceElementWithClone(matchedElement)
        : matchedElement

      const element = stripElementEventListeners(
        baseElement,
        { removeAll: true },
        { cloneNode: !options.replaceWithClone }
      )

      const wrapper = element.parentElement
      if (!wrapper) {
        searchErrorLogger.log('bindInputBridge: Unable to find wrapper element for input')
        return { success: false }
      }

      wrapper.setAttribute(WRAPPER_ATTRIBUTE, 'true')

      element.setAttribute(INPUT_ATTRIBUTE, 'true')
      element.classList.add(INPUT_CLASS)
      element.setAttribute('autocomplete', 'off')

      const mobileViewStore = useMobileViewStore(pinia)
      mobileViewStore.updateIsMobileView()
      if (!mobileViewStore.isMobileView) {
        element.style.position = element.style.position || 'relative'
        element.style.zIndex = '999999'
      }

      const updateBreakpointPlacement = () => {
        if (typeof options.forceBodyPlacementBelowViewportWidth !== 'number') {
          bridgeStore.setBreakpointBodyPlacement(false)
          return
        }

        bridgeStore.setBreakpointBodyPlacement(
          typeof window !== 'undefined' &&
          window.innerWidth <= options.forceBodyPlacementBelowViewportWidth
        )
      }

      updateBreakpointPlacement()

      let root = ensureAutocompleteRoot(wrapper, element, preferBodyPlacement(mobileViewStore, options, bridgeStore))
      root.setAttribute('aria-hidden', 'false')

      const collector = createCleanupCollector()
      let pendingPointerUpSuppress: ((event: Event) => void) | null = null
      const unregisterWrapperSafe = popupStore.registerSafeElement(wrapper)
      collector.add(unregisterWrapperSafe)
      const unregisterInputSafe = popupStore.registerSafeElement(element)
      collector.add(unregisterInputSafe)
      let unregisterRootSafe: (() => void) | null = popupStore.registerSafeElement(root)
      const removeRootSafe = () => {
        if (unregisterRootSafe) {
          unregisterRootSafe()
          unregisterRootSafe = null
        }
      }
      collector.add(removeRootSafe)

      const shouldSkipPlaceholderUpdate = Boolean(
        typeof window !== 'undefined' &&
        (window as any)?._QONizer?.customSmartSearchConfig?.skipInputPlaceholderUpdate
      )

      const applyPlaceholder = () => {
        if (shouldSkipPlaceholderUpdate) {
          return
        }

        const autocompleteTranslations = smartSearchTranslations.value?.autocomplete as Record<string, unknown> | undefined
        const placeholder = autocompleteTranslations?.whatSearch
        if (typeof placeholder === 'string') {
          if (element.getAttribute('placeholder') !== placeholder) {
            element.setAttribute('placeholder', placeholder)
          }
          if (element.placeholder !== placeholder) {
            element.placeholder = placeholder
          }
        }
      }

      if (!shouldSkipPlaceholderUpdate) {
        applyPlaceholder()
      }

      const stopPlaceholderWatch = shouldSkipPlaceholderUpdate
        ? () => {
            /* placeholder updates intentionally skipped */
          }
        : watch(
          () => (smartSearchTranslations.value?.autocomplete as Record<string, unknown> | undefined)?.whatSearch,
          () => {
            applyPlaceholder()
          }
        )
      collector.add(stopPlaceholderWatch)

      const searchQueryStore = useSearchQueryStore(pinia)

      const updatePlacement = () => {
        bridgeStore.updatePopupPlacement()
      }

      const repositionRoot = () => {
        removeRootSafe()
        root = ensureAutocompleteRoot(wrapper, element, preferBodyPlacement(mobileViewStore, options, bridgeStore))
        unregisterRootSafe = popupStore.registerSafeElement(root)
      }

      let rebinding = false
      const scheduleRebindIfDetached = () => {
        if (!options.observeViewport || rebinding || element.isConnected) {
          return
        }
        rebinding = true
        void bindInputBridge(selector, options).finally(() => {
          rebinding = false
        })
      }

      const handleViewportChange = () => {
        mobileViewStore.updateIsMobileView()
        updateBreakpointPlacement()
        repositionRoot()
        updatePlacement()
        scheduleRebindIfDetached()
      }

      const stopMobileSubscription = mobileViewStore.$subscribe(() => {
        updateBreakpointPlacement()
        repositionRoot()
        updatePlacement()
        scheduleRebindIfDetached()
      })

      window.addEventListener('resize', handleViewportChange)
      collector.add(() => {
        window.removeEventListener('resize', handleViewportChange)
      })

      collector.add(() => stopMobileSubscription())

      collector.add(() => {
        if (typeof window !== 'undefined' && pendingPointerUpSuppress) {
          window.removeEventListener('pointerup', pendingPointerUpSuppress, true)
          pendingPointerUpSuppress = null
        }
      })

      const stopBridgeSubscription = bridgeStore.$subscribe((mutation) => {
        if (mutation.storeId !== bridgeStore.$id) {
          return
        }

        const events = Array.isArray(mutation.events) ? mutation.events : []
        const hasBodyPlacementChange = events.some((event) => {
          if (typeof event.key !== 'string') {
            return false
          }
          return event.key === 'forceBodyPlacementHiddenInput' ||
            event.key === 'forceBodyPlacementBreakpoint'
        })

        if (!hasBodyPlacementChange) {
          return
        }

        repositionRoot()
        updatePlacement()
      })

      collector.add(() => stopBridgeSubscription())

      attachListeners(element, [
        {
          type: 'pointerdown',
          listener: (event: Event) => {
            const pointerEvent = event as PointerEvent
            if (typeof window !== 'undefined') {
              if (pendingPointerUpSuppress) {
                window.removeEventListener('pointerup', pendingPointerUpSuppress, true)
                pendingPointerUpSuppress = null
              }
              const suppressPointerUp = (upEvent: Event) => {
                preventClientAction(upEvent)
                window.removeEventListener('pointerup', suppressPointerUp, true)
                if (pendingPointerUpSuppress === suppressPointerUp) {
                  pendingPointerUpSuppress = null
                }
              }
              pendingPointerUpSuppress = suppressPointerUp
              window.addEventListener('pointerup', suppressPointerUp, true)
            }
            preventClientAction(pointerEvent)
            popupStore.showAutocomplete()
            updatePlacement()
          },
          options: true
        },
        {
          type: 'click',
          listener: (event: Event) => {
            preventClientAction(event)
          },
          options: true
        },
        {
          type: 'focus',
          listener: (event: Event) => {
            preventClientAction(event)
            popupStore.showAutocomplete()
            updatePlacement()
          },
          options: true
        },
        {
          type: 'input',
          listener: (event: Event) => {
            preventClientAction(event)
            const target = event.target as HTMLInputElement
            searchQueryStore.updateQuery(target.value)
            updatePlacement()
          }
        },
        {
          type: 'keyup',
          listener: (event: Event) => {
            preventClientAction(event)
            const keyboardEvent = event as KeyboardEvent
            if (keyboardEvent.key === 'Enter') {
              performSearch()
              return
            }
            popupStore.handleKeyUp()
          }
        },
        {
          type: 'keydown',
          listener: (event: Event) => {
            const keyboardEvent = event as KeyboardEvent
            if (keyboardEvent.key === 'Enter' || keyboardEvent.key === 'Escape') {
              preventClientAction(event)
            }
            if (keyboardEvent.key === 'Escape') {
              popupStore.hideAutocomplete()
            }
          }
        }
      ], collector)

      syncInputValueWithStore(element, collector, pinia)

      if (element.form instanceof HTMLFormElement) {
        const submitHandler = (event: Event) => {
          preventClientAction(event)
          performSearch()
        }
        element.form.addEventListener('submit', submitHandler, true)
        collector.add(() => {
          element.form?.removeEventListener('submit', submitHandler, true)
        })
      }

      collector.add(() => {
        root.removeAttribute('aria-hidden')
      })

      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(() => {
          updatePlacement()
        })
        resizeObserver.observe(element)
        resizeObserver.observe(wrapper)
        collector.add(() => resizeObserver.disconnect())
      }

      bridgeStore.setClientInput(element, wrapper, collector.list)
      updatePlacement()

      if (searchQueryStore.searchQuery && element.value !== searchQueryStore.searchQuery) {
        element.value = searchQueryStore.searchQuery
      }

      searchLogger.log(`bindInputBridge completed successfully for ${selector}`)
      return { success: true }
    } catch (error) {
      searchErrorLogger.log('bindInputBridge encountered an unexpected error', error)
      return { success: false }
    }
  }

  async function bindSearchButton (selector: string, optionsOrFlag?: BindingOptionsInput<BridgeBindingBaseOptions>): Promise<BindResult> {
    const options = normalizeBindingOptions<BridgeBindingBaseOptions>(optionsOrFlag)
    const popupStore = usePopupStore(pinia)

    try {
      searchLogger.log(`bindSearchButton: waiting for selector ${selector}`)
      const { element: matchedElement, attempts } = await waitForSelector(
        selector,
        (candidate): candidate is HTMLElement => candidate instanceof HTMLElement,
        options.wait
      )

      if (!matchedElement) {
        searchLogger.log(`bindSearchButton: selector ${selector} not resolved after ${attempts} attempts`)
        searchErrorLogger.log(`bindSearchButton: Element not found for selector ${selector}`)
        return { success: false }
      }

      searchLogger.log(`bindSearchButton: selector ${selector} resolved after ${attempts} attempt(s)`)

      const baseElement = options.replaceWithClone
        ? replaceElementWithClone(matchedElement)
        : matchedElement

      const element = stripElementEventListeners(
        baseElement,
        { eventTypes: CLICK_RELATED_EVENTS },
        { cloneNode: !options.replaceWithClone }
      )
      popupStore.registerSafeElement(element)
      popupStore.registerSafeElement(element)

      const handler = (event: Event) => {
        preventClientAction(event)
        performSearch()
      }

      const keydownHandler = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          handler(event)
        }
      }

      element.addEventListener('click', handler, true)
      element.addEventListener('keydown', keydownHandler, true)

      let rebinding = false
      const scheduleRebindIfDetached = () => {
        if (!options.observeViewport || rebinding || element.isConnected) {
          return
        }
        rebinding = true
        void bindSearchButton(selector, options).finally(() => {
          rebinding = false
        })
      }

      const viewportCleanup = () => {
        window.removeEventListener('resize', scheduleRebindIfDetached)
      }

      if (options.observeViewport) {
        window.addEventListener('resize', scheduleRebindIfDetached)
      }

      bridgeStore.setSearchButton(element, () => {
        element.removeEventListener('click', handler, true)
        element.removeEventListener('keydown', keydownHandler, true)
        viewportCleanup()
      })

      searchLogger.log(`bindSearchButton completed successfully for ${selector}`)
      return { success: true }
    } catch (error) {
      searchErrorLogger.log('bindSearchButton encountered an unexpected error', error)
      return { success: false }
    }
  }

  async function bindTrigger (selector: string, optionsOrFlag?: BindingOptionsInput<TriggerBindingOptions>): Promise<BindResult> {
    const options = normalizeBindingOptions<TriggerBindingOptions>(optionsOrFlag)
    const activationWidth = options.activateBelowViewportWidth === null
      ? null
      : (typeof options.activateBelowViewportWidth === 'number'
        ? options.activateBelowViewportWidth
        : DEFAULT_TRIGGER_ACTIVATION_WIDTH)

    try {
      searchLogger.log(`bindTrigger: waiting for selector ${selector}`)
      const { element: matchedElement, attempts } = await waitForSelector(
        selector,
        (candidate): candidate is HTMLElement => candidate instanceof HTMLElement,
        options.wait
      )

      if (!matchedElement) {
        searchLogger.log(`bindTrigger: selector ${selector} not resolved after ${attempts} attempts`)
        searchErrorLogger.log(`bindTrigger: Element not found for selector ${selector}`)
        return { success: false }
      }

      searchLogger.log(`bindTrigger: selector ${selector} resolved after ${attempts} attempt(s)`)

      const baseElement = options.replaceWithClone
        ? replaceElementWithClone(matchedElement)
        : matchedElement

      const element = stripElementEventListeners(
        baseElement,
        { eventTypes: CLICK_RELATED_EVENTS },
        { cloneNode: !options.replaceWithClone }
      )

      let root = document.getElementById('smartsearch-autocomplete')
      if (!root) {
        root = document.createElement('div')
        root.id = 'smartsearch-autocomplete'
        document.body.appendChild(root)
        searchLogger.log('Created smartsearch-autocomplete container for bridge')
      }

      const popupStore = usePopupStore(pinia)

      const handler = (event: Event) => {
        preventClientAction(event)
        if (popupStore.showAutocompletePopup) {
          popupStore.hideAutocomplete()
          return
        }

        popupStore.showAutocomplete()
      }

      const keydownHandler = (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          handler(event)
        }
      }

      let isBoundForViewport = false
      const attachListeners = () => {
        if (isBoundForViewport) {
          return
        }
        element.addEventListener('click', handler, true)
        element.addEventListener('keydown', keydownHandler, true)
        isBoundForViewport = true
      }

      const detachListeners = () => {
        if (!isBoundForViewport) {
          return
        }
        element.removeEventListener('click', handler, true)
        element.removeEventListener('keydown', keydownHandler, true)
        isBoundForViewport = false
      }

      const shouldBindForViewport = () => {
        if (activationWidth === null || typeof window === 'undefined') {
          return true
        }
        return window.innerWidth <= activationWidth
      }

      const evaluateViewportBinding = () => {
        if (shouldBindForViewport()) {
          attachListeners()
        } else {
          detachListeners()
        }
      }

      evaluateViewportBinding()

      let rebinding = false
      const scheduleRebindIfDetached = () => {
        if (!options.observeViewport || rebinding || element.isConnected) {
          return
        }
        rebinding = true
        void bindTrigger(selector, options).finally(() => {
          rebinding = false
        })
      }

      let viewportResizeListenerAttached = false
      if (activationWidth !== null) {
        window.addEventListener('resize', evaluateViewportBinding)
        viewportResizeListenerAttached = true
      }

      let observeViewportListenerAttached = false
      if (options.observeViewport) {
        window.addEventListener('resize', scheduleRebindIfDetached)
        observeViewportListenerAttached = true
      }

      const viewportCleanup = () => {
        if (viewportResizeListenerAttached) {
          window.removeEventListener('resize', evaluateViewportBinding)
          viewportResizeListenerAttached = false
        }
        if (observeViewportListenerAttached) {
          window.removeEventListener('resize', scheduleRebindIfDetached)
          observeViewportListenerAttached = false
        }
      }

      bridgeStore.setTrigger(element, () => {
        detachListeners()
        viewportCleanup()
      })

      searchLogger.log(`bindTrigger completed successfully for ${selector}`)
      return { success: true }
    } catch (error) {
      searchErrorLogger.log('bindTrigger encountered an unexpected error', error)
      return { success: false }
    }
  }

  async function focusAfterClick (selector: string, optionsOrFlag?: BindingOptionsInput<FocusAfterClickOptions>): Promise<BindResult> {
    const normalized = normalizeBindingOptions<FocusAfterClickOptions>(optionsOrFlag)
    const popupStore = usePopupStore(pinia)
    const shouldCloneElement = typeof optionsOrFlag === 'boolean'
      ? optionsOrFlag
      : optionsOrFlag?.replaceWithClone ?? false
    const options = {
      ...normalized,
      replaceWithClone: shouldCloneElement
    }

    const delayMs = Math.max(0, options.delayMs ?? DEFAULT_FOCUS_DELAY_MS)
    const eventTypes = options.events && options.events.length > 0
      ? options.events
      : DEFAULT_FOCUS_EVENTS
    const normalizedEventTypes = Array.from(new Set(eventTypes.map(eventName => eventName as string)))

    try {
      searchLogger.log(`focusAfterClick: waiting for selector ${selector}`)
      const { element: matchedElement, attempts } = await waitForSelector(
        selector,
        (candidate): candidate is HTMLElement => candidate instanceof HTMLElement,
        options.wait
      )

      if (!matchedElement) {
        searchLogger.log(`focusAfterClick: selector ${selector} not resolved after ${attempts} attempts`)
        searchErrorLogger.log(`focusAfterClick: Element not found for selector ${selector}`)
        return { success: false }
      }

      searchLogger.log(`focusAfterClick: selector ${selector} resolved after ${attempts} attempt(s)`)

      const element = options.replaceWithClone
        ? stripElementEventListeners(
          replaceElementWithClone(matchedElement),
          { eventTypes: normalizedEventTypes },
          { cloneNode: false }
        )
        : matchedElement
      popupStore.registerSafeElement(element)

      const collector = createCleanupCollector()

      const focusHandler = (_event: Event) => {
        window.setTimeout(() => {
          bridgeStore.focusClientInput()
        }, delayMs)
      }

      normalizedEventTypes.forEach(eventType => {
        element.addEventListener(eventType, focusHandler, true)
        collector.add(() => element.removeEventListener(eventType, focusHandler, true))
      })

      let rebinding = false
      const scheduleRebindIfDetached = () => {
        if (!options.observeViewport || rebinding || element.isConnected) {
          return
        }
        rebinding = true
        void focusAfterClick(selector, options).finally(() => {
          rebinding = false
        })
      }

      const viewportCleanup = () => {
        window.removeEventListener('resize', scheduleRebindIfDetached)
      }

      if (options.observeViewport) {
        window.addEventListener('resize', scheduleRebindIfDetached)
      }

      bridgeStore.setFocusActivator(element, () => {
        collector.runAll()
        viewportCleanup()
      })

      searchLogger.log(`focusAfterClick completed successfully for ${selector}`)
      return { success: true }
    } catch (error) {
      searchErrorLogger.log('focusAfterClick encountered an unexpected error', error)
      return { success: false }
    }
  }

  return {
    bindInputBridge,
    bindSearchButton,
    bindTrigger,
    focusAfterClick
  }
}
